<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        <!-- HEADER -->
        <div class="mb-8">
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">Notas Fiscais</h1>
          <p class="text-sm text-slate-500 font-medium">Gestão e consulta de NF-e emitidas</p>
        </div>

        <!-- ÁREA DE PESQUISA -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-magnify</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              FILTROS DE PESQUISA
            </h2>
          </div>
          <div class="p-8">
            <v-row>
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                <Date
                  @dateValue="[(filter.startDate = $event)]"
                  label="Data Inicial"
                />
              </v-col>
              <v-col cols="12" sm="12" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                <Date @dateValue="[(filter.endDate = $event)]" label="Data Final" />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Município</label>
                <v-autocomplete
                  :items="prefectures"
                  placeholder="SELECIONE O MUNICÍPIO..."
                  v-model="filter.prefecture_id"
                  item-value="prefecture_id"
                  item-text="prefecture_social_name"
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                  @change="getProviders(filter.prefecture_id)"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                <v-autocomplete
                  :items="providers"
                  placeholder="SELECIONE O FORNECEDOR..."
                  v-model="filter.provider_id"
                  item-value="provider_id"
                  item-text="provider_name"
                  solo flat dense hide-details
                  class="custom-select"
                  height="44"
                />
              </v-col>
            </v-row>
            <div class="flex justify-end gap-3 mt-8">
              <button
                @click="sendSearch"
                class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/10 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-10 h-10 transition-all transform hover:-translate-y-0.5 font-bold text-[11px] uppercase tracking-widest"
              >
                <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-filter-outline</v-icon>
                FILTRAR
              </button>
              <v-btn
                color="secondary"
                class="rounded-lg h-10"
                depressed
                target="_blank"
                :to="`/nfe/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}`"
                >RELATÓRIO</v-btn
              >
            </div>
          </div>
        </section>
        <!-- ÁREA DE LISTAGEM -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="bg-slate-700 px-6 py-4 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                RELAÇÃO DE NOTAS FISCAIS
              </h2>
            </div>
            <div class="flex items-center gap-2 flex-1 justify-end">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <button v-bind="attrs" v-on="on" @click="sendSearch" class="p-2 rounded-lg text-slate-300 hover:bg-slate-600 hover:text-white transition-colors">
                    <v-icon color="currentColor">mdi-history</v-icon>
                  </button>
                </template>
                <span>Atualizar</span>
              </v-tooltip>

              <div class="h-6 border-r border-slate-500 mx-2"></div>

              <div class="flex-none">
                <button 
                  @click="$router.push('/nfe/create')" 
                  class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                >
                  <v-icon color="white" small>mdi-plus</v-icon>
                  <span class="font-bold text-sm uppercase">NOVO</span>
                </button>
              </div>

              <button @click="showSearch = !showSearch" class="p-2 rounded-lg text-slate-300 hover:bg-slate-600 hover:text-white transition-colors ml-2">
                <v-icon color="currentColor">mdi-magnify</v-icon>
              </button>
            </div>
          </div>

          <div v-if="showSearch" class="p-6 bg-slate-50 border-b border-slate-200">
            <v-text-field
              v-model="search"
              placeholder="PESQUISAR NAS NOTAS..."
              solo flat dense hide-details
              prepend-inner-icon="mdi-magnify"
              background-color="white"
              class="custom-select"
              height="48"
            />
          </div>

          <div class="p-0">
            <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :items-per-page="10"
              :loading="loader"
              class="custom-table"
              hide-default-footer
            >
              <template v-slot:item.supply_code="{ item }">
                <span class="text-xs font-bold text-slate-500">{{
                  item.supply_code ? item.supply_code : item.vehicle_password
                }}</span>
              </template>
              <template v-slot:item.datehour="{ item }">
                <span class="text-xs text-slate-600 font-semibold">{{ convertDateTime(item.datehour) }}</span>
              </template>
              <template v-slot:item.supply_bidding_price="{ item }">
                <span class="font-semibold text-slate-600">{{
                  item.supply_bidding_price
                    ? realMask(item.supply_bidding_price.toFixed(2))
                    : 0
                }}</span>
              </template>
              <template v-slot:item.supply_liters_stocked="{ item }">
                <span class="font-black text-slate-700">{{
                  item.supply_liters_stocked
                    ? realMask(item.supply_liters_stocked.toFixed(2))
                    : 0
                }}</span>
              </template>
              <template v-slot:item.supply_bidding_value="{ item }">
                <span class="font-black text-slate-900">R$ {{
                  item.supply_bidding_value
                    ? realMask(item.supply_bidding_value.toFixed(2))
                    : 0
                }}</span>
              </template>

              <template v-slot:item.model_desc="{ item }">
                <div class="flex flex-col">
                  <span class="font-bold text-slate-800 text-sm tracking-tight">{{ item.vehicle_board }}</span>
                  <span class="text-[10px] text-slate-400 uppercase font-bold">{{ item.brand_desc }} • {{ item.model_desc }}</span>
                </div>
              </template>

              <template v-slot:item.action="{ item }">
                <div class="flex items-center justify-end gap-2">
                  <button
                    @click="$router.push(`/nfe/edit/${item.supply_id}`)"
                    class="p-2 rounded-lg text-slate-400 hover:bg-orange-50 hover:text-orange-500 transition-all"
                    title="Editar"
                  >
                    <v-icon small color="currentColor">mdi-pencil-outline</v-icon>
                  </button>
                  <button
                    @click="deleteItem(item.supply_id)"
                    class="p-2 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-all"
                    title="Excluir"
                  >
                    <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                  </button>
                </div>
              </template>
            </v-data-table>

            <!-- FOOTER COM TOTAIS -->
            <div class="p-8 bg-slate-50 border-t border-slate-200 flex justify-end">
              <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-6">
                <div>
                  <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-1">TOTAL ACUMULADO</span>
                  <span class="text-2xl font-black text-slate-700">R$ {{ total ? realMask(total) : '0,00' }}</span>
                </div>
                <v-icon size="40" color="orange-100" style="opacity: 0.5">mdi-currency-usd</v-icon>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";
export default {
  mixins: [Helpers],
  components: { Date },
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      dialog: false,
      copiado: false,
      password: "",
      showPassword: false,
      data: [],
      providers: [],
      prefectures: [],
      items: [],
      vehicles: [],
      fieldRules: [(v) => !!v || "Este campo é requerido"],
      prefecture_id: null,
      total: 0,
      filter: {
        endDate: "",
        startDate: "",
        prefecture_id: null,
        provider_id: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Data/Hora", align: "start", value: "datehour" },
        { text: "Número", value: "supply_coupon" },
        { text: "Veículo", value: "model_desc" },
        { text: "Item", value: "item_description_desc" },
        { text: "Fornecedor", value: "provider_name" },
        { text: "Preço", value: "supply_bidding_price" },
        { text: "Litros", value: "supply_liters_stocked" },
        { text: "Valor", value: "supply_bidding_value" },
        { text: "Ações", align: "right", value: "action" },
      ],
    };
  },
  created() {
    this.Helpers;
    this.loader = false;
    this.$store.dispatch("setLoader", this.loader);
    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
  },
  methods: {
    sendSearch() {
      if (!this.filter.startDate || !this.filter.endDate || !this.filter.prefecture_id || !this.filter.provider_id) {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Selecione todos os filtros para realizar a pesquisa!";
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
        return;
      }
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .post("/billings/nfes/data", this.filter)
        .then((r) => {
          this.data = r.data;
          let total = 0;
          this.data.map((v) => {
            total += parseFloat(v.supply_bidding_value);
          });
          this.total = total.toFixed(2);
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = e.message;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    getProviders(prefecture_id) {
      this.$http
        .get(`/providers/prefecture/${prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/billings/nfes/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.sendSearch();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.top-bar {
  padding: 30px 15px;
}
.title {
  text-transform: uppercase;
  font-weight: 400;
}
.maxPage {
  width: 100%;
}
.search-field {
  margin-top: 10px;
}
.space {
  margin: 0 10px;
}
.search-input-nfe >>> .v-input__control {
  min-height: 36px !important;
}
.search-input-nfe >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
}

.custom-select >>> .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

.custom-table >>> th {
  background-color: #f1f5f9 !important;
  text-transform: uppercase;
  font-size: 10px !important;
  font-weight: 800 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 48px !important;
}

.custom-table >>> td {
  font-size: 12px !important;
  color: #334155 !important;
  height: 60px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.custom-table >>> tr:hover {
  background-color: #f8fafc !important;
}
</style>